import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { SubpageLayout } from '../components/SubpageLayout';
import { creditRows, imprintRows, legalPages } from '../data/legal';

/** One template for all legal subpages: Imprint, Privacy Policy, Terms of Use, Cookie Policy. */
export function Legal() {
  const {
    slug
  } = useParams();
  const page = legalPages.find(p => p.slug === slug);
  if (!page) return <Navigate to="/legal/imprint" replace data-id="element-541" data-mp="a0ne8e6j2jqj" />;
  return <SubpageLayout title={page.title} data-id="element-542" data-mp="esey65v6hr7p">
      <div className="px-page pb-20 pt-36 md:pb-28 md:pt-44" data-id="element-543" data-mp="etgiz21zxs2m">
        <div className="grid gap-12 md:grid-cols-12 md:gap-x-6" data-id="element-544" data-mp="3iok4dh464z">
          {/* Left: title + legal navigation */}
          <aside className="md:col-span-4" data-id="element-545" data-mp="522fykau6a4q">
            <p className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/60" data-id="element-546" data-mp="52vdcunwnna7">Legal</p>
            <h1 className="mt-4 font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] md:text-[48px]" data-id="element-547" data-mp="3n909vuf72yei">
              {page.title}
            </h1>
            <p className="mt-3 text-[13px] text-fg/45" data-id="element-548" data-mp="27kd54pdvo948">Last updated {page.updated}</p>

            <nav aria-label="Legal pages" className="mt-10 md:sticky md:top-28" data-id="element-549" data-mp="23k2omisgm0b4">
              <ul className="border-t border-line" data-id="element-550" data-mp="2hjaprh86nihy">
                {legalPages.map(p => {
                const active = p.slug === page.slug;
                return <li key={p.slug} className="border-b border-line" data-id="element-551" data-mp="2ci4w9h6p7r7d">
                      <Link to={`/legal/${p.slug}`} aria-current={active ? 'page' : undefined} className={`block py-3 text-[15px] transition-colors duration-150 ${active ? 'text-fg' : 'text-fg/45 hover:text-fg'}`} data-id="element-552" data-mp="1oveumgy9hyna">
                        {p.title}
                      </Link>
                    </li>;
              })}
              </ul>
            </nav>
          </aside>

          {/* Right: content */}
          <article className="md:col-span-7 md:col-start-6" data-id="element-553" data-mp="3183odtgfhi0n">
            {page.slug === 'imprint' && <dl className="mb-14 border-t border-line" data-id="element-554" data-mp="365ln6r0qegux">
                {imprintRows.map(row => <div key={row.label} className="grid gap-1 border-b border-line py-4 md:grid-cols-[220px_1fr] md:gap-6" data-id="element-555" data-mp="3ay6e9ks96fur">
                    <dt className="text-[13px] text-fg/45" data-id="element-556" data-mp="3ey719ttf3zdf">{row.label}</dt>
                    <dd className="text-[16px] leading-[1.45] text-fg" data-id="element-557" data-mp="2i9hky7xfvetk">
                      {row.href ? <a href={row.href} className="underline decoration-fg/30 underline-offset-4 transition-colors duration-150 hover:decoration-fg" data-id="element-558" data-mp="2n8c3r9vep3do">
                          {row.value}
                        </a> : row.value}
                    </dd>
                  </div>)}
              </dl>}

            <div className="space-y-10" data-id="element-559" data-mp="lm6pszqwto5x">
              {page.sections.map(s => <section key={s.heading} data-id="element-560" data-mp="gmbxqsveesja">
                  <h2 className="text-[19px] leading-[1.3] tracking-[-0.01em] text-fg md:text-[20px]" data-id="element-561" data-mp="gndkhuls13gu">{s.heading}</h2>
                  <div className="mt-3 max-w-[640px] space-y-4 text-[15px] leading-[1.6] text-fg/60" data-id="element-562" data-mp="bu0vhjd6qunk">
                    {s.body.split('\n\n').map((para, i) => <p key={i} data-id="element-563" data-mp="2w8hqjn43v2a" data-mpi={i}>{para}</p>)}
                  </div>
                </section>)}
            </div>

            {page.slug === 'imprint' && <section className="mt-14" aria-labelledby="website-credits" data-id="element-564" data-mp="3p3otcuzng90j">
                <h2 id="website-credits" className="text-[19px] leading-[1.3] tracking-[-0.01em] text-fg md:text-[20px]" data-id="element-565" data-mp="3l1k2u54hzsi4">
                  Website credits
                </h2>
                <dl className="mt-5 border-t border-line" data-id="element-566" data-mp="1j9p7j02ju0m2">
                  {creditRows.map(row => <div key={row.label} className="grid gap-1 border-b border-line py-4 md:grid-cols-[220px_1fr] md:gap-6" data-id="element-567" data-mp="1qcvn7h2bq2qq">
                      <dt className="text-[13px] text-fg/45" data-id="element-568" data-mp="1vc8dycwbftyl">{row.label}</dt>
                      <dd className="text-[16px] leading-[1.45] text-fg" data-id="element-569" data-mp="1hch1whpd0nn8">
                        {row.href ? <a href={row.href} target={row.external ? '_blank' : undefined} rel={row.external ? 'noopener noreferrer' : undefined} className="underline decoration-fg/30 underline-offset-4 transition-colors duration-150 hover:decoration-fg" data-id="element-570" data-mp="1ld99l6il2khe">
                            {row.value}
                            {row.external && <span className="sr-only" data-id="element-571" data-mp="12l86tt2oua2p"> (opens in a new tab)</span>}
                          </a> : row.value}
                      </dd>
                    </div>)}
                </dl>
              </section>}
          </article>
        </div>
      </div>
    </SubpageLayout>;
}